import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerInternal, uiMarkerLayout } from './shared';

/**
 * PITCH
 *
 * The prospect's sales page: the page a tow company opens from a text after a
 * sales call. Everything here sits inside PitchShell, which switches on the
 * `theme-sales` tokens (cream ground, deep green, Schibsted Grotesk, soft
 * corners), so the classes below say *which* surface and the theme says what
 * it looks like.
 */

/** The page itself: the theme, the ground, the typeface. */
export const pitchShellRecipe = cva(
  [
    uiMarker,
    uiMarkerLayout,
    'theme-sales w-full bg-background font-sans text-foreground antialiased',
  ],
  {
    variants: {
      /**
       * `page` fills the viewport. `inline` is the same theme on a block, for
       * the /library specimens.
       */
      fill: {
        page: 'min-h-screen',
        inline: '',
      },
    },
    defaultVariants: { fill: 'page' },
  }
);

/**
 * One band of the page: a centred column with the page gutters. 16px a side
 * on a phone, 24px from md, and never wider than 1152px (672px when narrow).
 */
export const pitchSectionRecipe = cva(
  [uiMarker, uiMarkerLayout, 'mx-auto w-full px-4 md:px-6'],
  {
    variants: {
      /** `page` is the full column. `narrow` is a reading width, for a letter. */
      width: {
        page: 'max-w-6xl',
        narrow: 'max-w-2xl',
      },
      /** Room above and below the band's content. */
      space: {
        none: '',
        sm: 'py-6',
        md: 'py-10',
        lg: 'py-12 md:py-16',
        video: 'pb-12',
        closing: 'pb-12 md:pb-16',
        footer: 'border-t border-border py-8',
      },
    },
    defaultVariants: { space: 'none', width: 'page' },
  }
);

/**
 * The opening. `split`: headline on the left, the plain-words line on the
 * right, on the same baseline, one column on a phone. `center`: both
 * centred, one under the other.
 */
export const pitchHeroRecipe = cva([uiMarker, uiMarkerLayout], {
  variants: {
    align: {
      split: 'grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-end md:gap-12',
      center: 'mx-auto flex max-w-3xl flex-col items-center gap-6',
    },
  },
  defaultVariants: { align: 'split' },
});

/** The headline's side of the hero: the chip above, the headline under it. */
export const pitchHeroLeadRecipe = cva(
  [uiMarkerInternal, 'flex flex-col gap-5'],
  {
    variants: {
      align: { split: 'items-start', center: 'items-center' },
    },
    defaultVariants: { align: 'split' },
  }
);

/** A card on the cream ground: a pain point, a feature. */
export const pitchCardRecipe = cva([
  uiMarker,
  'flex flex-col gap-5 rounded-3xl bg-card p-6 text-card-foreground',
]);

/** A card's title and its line or two under it. */
export const pitchCardTextRecipe = cva([
  uiMarkerInternal,
  'flex flex-col gap-1.5',
]);

/** The chip above a card's title, kept to its own width. */
export const pitchCardEyebrowRecipe = cva([uiMarkerInternal, 'flex pb-1']);

/**
 * The small, static stand-in for a screen inside a feature card: a driver
 * list, an invoice. A shade darker than the card, so it reads as "inside".
 */
export const mockPanelRecipe = cva([
  uiMarker,
  'flex flex-col gap-3 rounded-2xl bg-background p-4 font-sans text-body-sm text-foreground',
]);

/** Something the prospect said on the call, set apart from the copy. */
export const pullQuoteRecipe = cva([
  uiMarker,
  'm-0 flex flex-col gap-2 border-l-2 border-primary/30 pl-4',
]);

/** The closing band: deep green, cream type, the last call to act. */
export const pitchPanelRecipe = cva([
  uiMarker,
  'flex flex-col gap-8 rounded-3xl bg-primary p-8 text-primary-foreground md:flex-row md:items-end md:justify-between md:p-12',
]);

/** The positioned frame around the video. */
export const pitchVideoFrameRecipe = cva([uiMarker, 'relative']);

export const pitchFooterBrandRecipe = cva([
  uiMarker,
  'text-foreground tracking-tight hover:text-primary',
]);
export const pitchFooterBrandTextRecipe = cva([
  uiMarkerInternal,
  'font-semibold',
]);

/** Static placeholders use the same layout and surfaces as the loaded page. */
export const pitchSkeletonPlayerRecipe = cva([
  uiMarker,
  'overflow-hidden rounded-3xl bg-secondary',
]);
export const pitchSkeletonControlsRecipe = cva([
  uiMarkerInternal,
  'flex flex-col gap-4 p-5',
]);
export const pitchSkeletonHeadlineRecipe = cva([
  uiMarkerInternal,
  'flex h-52 flex-col gap-1.5 md:h-[17rem]',
]);
export const pitchSkeletonHeadlineLineRecipe = cva([
  uiMarkerInternal,
  'min-h-0 flex-1',
]);
export const pitchSkeletonBylineRecipe = cva([
  uiMarkerInternal,
  'pt-8',
]);
export const pitchSkeletonPieceRecipe = cva([uiMarkerInternal], {
  variants: {
    part: {
      brand: 'h-6 w-24',
      separator: 'size-2',
      companyMark: 'size-10',
      companyName: 'h-5 w-40',
      headerAction: 'h-11 w-32',
      eyebrow: 'h-8 w-64 max-w-full',
      headlineFull: 'h-full w-full',
      headlineShort: 'h-full w-[92%]',
      headlineNearFull: 'h-full w-[96%]',
      headlineEnd: 'h-full w-[75%]',
      descriptionFull: 'h-5 w-full',
      descriptionNearFull: 'h-5 w-[95%]',
      descriptionEnd: 'h-5 w-[72%]',
      video: 'aspect-video w-full rounded-none',
      seek: 'h-1.5 w-full',
      controls: 'h-5 w-40',
      repMark: 'size-10',
      repName: 'h-5 w-44',
      repRole: 'h-4 w-32',
      videoAction: 'ml-auto h-14 w-64 max-sm:w-32',
    },
  },
});

export type PitchShellRecipeProps = VariantProps<typeof pitchShellRecipe>;
export type PitchSectionRecipeProps = VariantProps<typeof pitchSectionRecipe>;
export type PitchHeroRecipeProps = VariantProps<typeof pitchHeroRecipe>;

/** The website mock's browser chrome: three dots and an address. */
export const mockBrowserBarRecipe = cva([
  uiMarkerInternal,
  'flex items-center gap-1.5 border-b border-border pb-3',
]);

export const mockBrowserDotRecipe = cva([
  uiMarkerInternal,
  'size-2 shrink-0 rounded-pill bg-input',
]);

/** The website mock's page: the company's name as the site title. */
export const mockBrowserPageRecipe = cva([
  uiMarkerInternal,
  'flex flex-col gap-2 rounded-xl bg-card p-3',
]);
